import { useState } from "react";
import { AlertTriangle, PackageX, Pencil, Plus, RefreshCw, Search, ShoppingCart, SlidersHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { IngredientEditor, field, label } from "./IngredientEditor";
import { LedgerList } from "./LedgerList";
import { LowStockAlertsCard } from "./LowStockAlertsCard";
import { WasteModal } from "./WasteModal";
import { fmtQty, fmtWhen, invApi } from "./api";

function AdjustModal({ token, ing, onDone, onClose }) {
  const [mode, setMode] = useState("set");
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(val);
    if (Number.isNaN(n)) return toast.error("Enter a number");
    setBusy(true);
    try {
      const body = { ingredient_id: ing.id, reason };
      if (mode === "set") body.new_stock = n; else body.delta = mode === "add" ? n : -n;
      const { data } = await invApi(token).post("/inventory/adjust", body);
      toast.success(`${data.name}: now ${fmtQty(data.current_stock)} ${data.unit}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't adjust");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="adjust-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-sm p-5 space-y-3">
        <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Adjust · {ing.name}</div>
        <div className="text-xs text-stone-500">On hand now: <b>{fmtQty(ing.current_stock)} {ing.unit}</b></div>
        <div className="grid grid-cols-3 gap-1 bg-stone-100 rounded-xl p-1">
          {[["set", "Set to"], ["add", "Add"], ["remove", "Remove"]].map(([k, t]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`adjust-mode-${k}`}
              className={`py-1.5 rounded-lg text-xs font-black uppercase tracking-widest ${mode === k ? "bg-white text-brand-red shadow-sm" : "text-stone-500"}`}>{t}</button>
          ))}
        </div>
        <input value={val} onChange={(e) => setVal(e.target.value)} type="number" step="any" inputMode="decimal" autoFocus data-testid="adjust-value-input"
          className="w-full px-4 py-3 rounded-2xl border-2 border-stone-200 text-2xl font-black text-center tabular-nums outline-none focus:border-brand-red" />
        <label className="block"><span className={label}>Reason (waste, count correction…)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} className={field} data-testid="adjust-reason-input" /></label>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border-2 border-stone-200 text-xs uppercase tracking-widest font-black text-stone-600">Cancel</button>
          <button type="submit" disabled={busy} data-testid="adjust-submit-btn" className="flex-1 py-3 rounded-full bg-brand-red text-white text-xs uppercase tracking-widest font-black disabled:opacity-50">Apply</button>
        </div>
      </form>
    </div>
  );
}

export function InventoryOverview({ token, data, reload, role = "admin", onShop }) {
  const [q, setQ] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [tossing, setTossing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const items = (data?.ingredients || []).filter((i) =>
    (!onlyLow || i.low_stock) && (!q || i.name.toLowerCase().includes(q.toLowerCase()) || (i.barcode || "").includes(q)),
  );

  const remove = async (i) => {
    if (!window.confirm(`Delete "${i.name}"? It will also be removed from every recipe.`)) return;
    try {
      await invApi(token).delete(`/inventory/ingredients/${i.id}`);
      toast.success("Ingredient deleted");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: r } = await invApi(token).post("/inventory/square-sync");
      toast.success(`Square: ${r.processed} new register order${r.processed === 1 ? "" : "s"} deducted`);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Square sync failed"); }
    finally { setSyncing(false); }
  };

  const sync = data?.square_sync;
  const unmatched = Object.entries(sync?.unmatched || {});

  return (
    <div className="space-y-4" data-testid="inventory-overview">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {[
          ["Ingredients", data?.ingredients?.length ?? 0, "text-stone-900", "stat-total"],
          ["Low stock", data?.low_count ?? 0, data?.low_count ? "text-red-600" : "text-stone-900", "stat-low"],
          ["Mapped items", data?.mapped_menu_items ?? 0, "text-stone-900", "stat-mapped"],
        ].map(([t, v, cls, tid]) => (
          <div key={t} className="bg-white border border-stone-200 rounded-2xl px-4 py-3">
            <div className={label}>{t}</div>
            <div className={`text-2xl font-black tabular-nums ${cls}`} data-testid={tid}>{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Raw ingredients</div>
          <div className="ml-auto flex items-center gap-2">
            {data?.low_count > 0 && onShop && (
              <button type="button" onClick={onShop} data-testid="open-shopping-list-btn"
                className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 flex items-center gap-1"><ShoppingCart className="w-3.5 h-3.5" /> Shop list</button>
            )}
            <button type="button" onClick={() => setOnlyLow((v) => !v)} data-testid="filter-low-btn"
              className={`text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full border ${onlyLow ? "bg-red-600 text-white border-red-600" : "border-stone-200 text-stone-600"}`}>
              Low only
            </button>
            <button type="button" onClick={() => setEditing({})} data-testid="add-ingredient-btn"
              className="bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
          </div>
        </div>
        <div className="px-4 py-2 border-b border-stone-100 flex items-center gap-2">
          <Search className="w-4 h-4 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or barcode" data-testid="ingredient-search" className="flex-1 text-sm outline-none bg-transparent py-1" />
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-stone-400" data-testid="ingredients-empty">
            {data?.ingredients?.length ? "Nothing matches." : "No ingredients yet — scan a delivery in Intake or tap Add."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="ingredients-table">
            <thead className="text-[10px] uppercase tracking-widest text-stone-500 font-bold bg-stone-50">
              <tr><th className="text-left px-4 py-2">Ingredient</th><th className="text-right px-2 py-2">On hand</th><th className="text-right px-2 py-2 hidden sm:table-cell">Alert at</th><th className="text-right px-2 py-2 hidden md:table-cell">Cost/unit</th><th className="px-2 py-2" /></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {items.map((i) => (
                <tr key={i.id} className={i.low_stock ? "bg-red-50/60" : ""} data-testid={`ingredient-row-${i.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {i.low_stock && <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" data-testid={`low-icon-${i.id}`} />}
                      <div className="min-w-0">
                        <div className={`font-bold truncate ${i.low_stock ? "text-red-700" : "text-stone-800"}`}>{i.name}</div>
                        <div className="text-[11px] text-stone-400 font-mono truncate">{i.barcode || "no barcode"}</div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-2 py-2.5 text-right font-black tabular-nums whitespace-nowrap ${i.low_stock ? "text-red-600" : "text-stone-900"}`} data-testid={`stock-${i.id}`}>
                    {fmtQty(i.current_stock)} <span className="text-[10px] text-stone-400 font-bold">{i.unit}</span>
                    {i.out_of_stock && <div className="text-[9px] uppercase tracking-widest font-black text-red-600">Out</div>}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-stone-500 hidden sm:table-cell">{fmtQty(i.low_stock_threshold)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums hidden md:table-cell">
                    <button type="button" onClick={() => setEditing(i)} data-testid={`cost-btn-${i.id}`} className={`text-xs font-bold underline decoration-dotted ${i.cost_per_unit == null ? "text-amber-600" : "text-stone-600"}`}>
                      {i.cost_per_unit == null ? "set cost" : `$${Number(i.cost_per_unit).toFixed(2)}`}
                    </button>
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="flex items-center justify-end gap-0.5">
                      <button type="button" title="Toss it (log waste)" onClick={() => setTossing(i)} data-testid={`toss-btn-${i.id}`} className="p-1.5 rounded-lg text-stone-500 hover:text-red-700 hover:bg-red-50"><PackageX className="w-4 h-4" /></button>
                      <button type="button" title="Adjust stock" onClick={() => setAdjusting(i)} data-testid={`adjust-btn-${i.id}`} className="p-1.5 rounded-lg text-stone-500 hover:text-brand-red hover:bg-red-50"><SlidersHorizontal className="w-4 h-4" /></button>
                      <button type="button" title="Edit" onClick={() => setEditing(i)} data-testid={`edit-btn-${i.id}`} className="p-1.5 rounded-lg text-stone-500 hover:text-brand-red hover:bg-red-50"><Pencil className="w-4 h-4" /></button>
                      <button type="button" title="Delete" onClick={() => remove(i)} data-testid={`delete-btn-${i.id}`} className="p-1.5 rounded-lg text-stone-400 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {role === "admin" && <LowStockAlertsCard token={token} />}

      {sync?.enabled && (
        <div className="bg-white border border-stone-200 rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="square-sync-card">
          <div className="flex-1 min-w-[180px]">
            <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Square register</div>
            <div className="text-[11px] text-stone-500">Auto-checks every 60 sec · last {fmtWhen(sync.last_sync_at)}</div>
            {unmatched.length > 0 && (
              <div className="text-[11px] text-amber-700 mt-1" data-testid="square-unmatched">
                Not matched to a menu item: {unmatched.slice(0, 6).map(([n, c]) => `${n} (${c})`).join(", ")}
              </div>
            )}
          </div>
          <button type="button" onClick={syncNow} disabled={syncing} data-testid="square-sync-btn"
            className="bg-stone-800 text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync now
          </button>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 font-black text-stone-800 text-sm uppercase tracking-widest">Recent activity</div>
        <LedgerList token={token} rows={data?.recent || []} onChanged={reload} compact />
      </div>

      {editing && <IngredientEditor token={token} initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {tossing && <WasteModal token={token} ingredient={tossing} onClose={() => setTossing(null)} onDone={() => { setTossing(null); reload(); }} />}
      {adjusting && <AdjustModal token={token} ing={adjusting} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); reload(); }} />}
    </div>
  );
}
